import { Alert, Loader, Stack, Text, TextInput } from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { RepositorySettingsForm } from "./RepositorySettingsForm";
import { useRepositorySettings } from "./useRepositorySettings";

// Секция настроек репозитория. Репозиторий выбирается свободным вводом
// `owner/repo` (зеркало `RepoTemplateSection`): per-(org, repo) настройки
// не завязаны на состояние Forgejo-подключения. Ссылка со страницы
// подключений ведёт сюда с предвыбором через `?repo=owner/repo`.
export function RepositorySettingsSection(): ReactElement {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [repoFullName, setRepoFullName] = useState(
    () => searchParams.get("repo") ?? "",
  );

  const trimmedRepo = repoFullName.trim();
  const repoEntered = trimmedRepo.length > 0;

  return (
    <Stack gap="md">
      <Text c="dimmed">{t("repositorySettings.intro")}</Text>

      <TextInput
        label={t("repositorySettings.repoLabel")}
        description={t("repositorySettings.repoHint")}
        placeholder={t("repositorySettings.repoPlaceholder")}
        value={repoFullName}
        onChange={(event) => {
          setRepoFullName(event.currentTarget.value);
        }}
      />

      {repoEntered ? (
        <RepositorySettingsBody repoFullName={trimmedRepo} />
      ) : (
        <Text c="dimmed">{t("repositorySettings.enterRepo")}</Text>
      )}
    </Stack>
  );
}

// Тело секции после ввода репо: грузит настройки, затем рендерит форму.
// Loader/Error до загрузки; форма перемонтируется по `key` (репо +
// has_row) после первого сохранения — «использует дефолты» исчезает.
function RepositorySettingsBody({
  repoFullName,
}: {
  readonly repoFullName: string;
}): ReactElement {
  const { t } = useTranslation();
  const settings = useRepositorySettings(repoFullName);

  if (settings.isPending) {
    return <Loader />;
  }
  if (settings.isError) {
    return (
      <Alert color="red" title={t("repositorySettings.errorHeading")}>
        {t("repositorySettings.loadingError")}
      </Alert>
    );
  }

  return (
    <RepositorySettingsForm
      key={`${repoFullName}:${String(settings.data.has_row)}`}
      repoFullName={repoFullName}
      data={settings.data}
    />
  );
}
